import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MediaCard, { MediaCardPost } from "@/components/MediaCard";
import { toPlainText } from "@/lib/text";

type Category =
  | "say_it_in_60"
  | "weekend_huddle"
  | "business_of_womens_sports"
  | "athlete_spotlight"
  | "originals";

type MediaPost = MediaCardPost & { category: Category };

type NewsRow = {
  id: string;
  title: string;
  url: string;
  summary: string | null;
  published_at: string | null;
  news_sources: { name: string } | null;
};

const SECTIONS: { key: Category; slug: string; label: string; tagline: string }[] = [
  { key: "say_it_in_60", slug: "say-it-in-60", label: "Say It In 60", tagline: "Top women's sports headlines in ~60 seconds." },
  { key: "weekend_huddle", slug: "weekend-huddle", label: "Weekend Huddle", tagline: "The stories, moments and matchups to know from the weekend." },
  { key: "business_of_womens_sports", slug: "business-of-womens-sports", label: "Business of Women's Sports", tagline: "Deals, media, sponsorships and the money moving the game." },
  { key: "athlete_spotlight", slug: "athlete-spotlight", label: "Athlete Spotlight", tagline: "Stories and profiles of athletes building visibility on and off the field." },
  { key: "originals", slug: "originals", label: "Originals", tagline: "Reporting and features written by Say It In Sports." },
];

const PREVIEW_LIMIT = 3;

const MediaHub = () => {
  const [posts, setPosts] = useState<MediaPost[]>([]);
  const [news, setNews] = useState<NewsRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [media, newsItems] = await Promise.all([
        supabase
          .from("media_posts")
          .select(
            "id, category, content_type, slug, title, subtitle, cover_image_path, thumbnail_url, video_url, published_at",
          )
          .eq("status", "published")
          .order("published_at", { ascending: false, nullsFirst: false }),
        supabase
          .from("news_items")
          .select("id, title, url, summary, published_at, news_sources(name)")
          .order("published_at", { ascending: false, nullsFirst: false })
          .limit(6),
      ]);
      setPosts((media.data as any) ?? []);
      setNews((newsItems.data as any) ?? []);
      setLoading(false);
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="py-16 md:py-24 border-b border-border/50">
          <div className="container">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Media Hub</span>
            <h1 className="text-5xl md:text-7xl mt-3 text-balance">Stories from Say It In Sports.</h1>
            <p className="text-muted-foreground mt-4 max-w-2xl">
              Original reporting, video and features on the women redefining sports — and the teams, leagues and brands
              building what comes next.
            </p>
            <div className="flex flex-wrap gap-2 mt-6">
              {SECTIONS.map((s) => (
                <Link
                  key={s.key}
                  to={`/media-hub/${s.slug}`}
                  className="px-4 py-2 rounded-full bg-muted text-sm font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors min-h-[44px] flex items-center"
                >
                  {s.label}
                </Link>
              ))}
              <a
                href="#news"
                className="px-4 py-2 rounded-full bg-muted text-sm font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors min-h-[44px] flex items-center"
              >
                News
              </a>
            </div>
          </div>
        </section>

        {SECTIONS.map((section) => {
          const all = posts.filter((p) => p.category === section.key);
          const items = all.slice(0, PREVIEW_LIMIT);
          return (
            <section key={section.key} id={section.slug} className="py-16 md:py-20 border-b border-border/30">
              <div className="container">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                  <div>
                    <h2 className="font-display text-4xl md:text-5xl">{section.label}</h2>
                    <p className="text-muted-foreground mt-2">{section.tagline}</p>
                  </div>
                  <Link to={`/media-hub/${section.slug}`} className="text-sm font-semibold text-primary hover:underline">
                    View all →
                  </Link>
                </div>

                {loading ? (
                  <p className="text-muted-foreground mt-10">Loading…</p>
                ) : items.length === 0 ? (
                  <p className="text-muted-foreground mt-10">Coming soon...</p>
                ) : (
                  <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
                      {items.map((p) => (
                        <MediaCard key={p.id} post={p} />
                      ))}
                    </div>
                    {all.length > PREVIEW_LIMIT && (
                      <div className="mt-8">
                        <Link
                          to={`/media-hub/${section.slug}`}
                          className="inline-block px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary transition-colors"
                        >
                          See all {all.length} {section.label} posts
                        </Link>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>
          );
        })}

        {news.length > 0 && (
          <section id="news" className="py-16 md:py-20">
            <div className="container">
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <h2 className="font-display text-4xl md:text-5xl">News</h2>
                  <p className="text-muted-foreground mt-2">
                    Headlines from across women's sports. Every link opens at the original publisher.
                  </p>
                </div>
                <Link to="/news" className="text-sm font-semibold text-primary hover:underline">
                  View all →
                </Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
                {news.map((n) => (
                  <a
                    key={n.id}
                    href={n.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-2xl bg-card shadow-card hover:shadow-bold transition-all p-5 block"
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">
                      {n.news_sources?.name ?? "News"}
                    </p>
                    <h3 className="font-display text-2xl leading-tight mt-2">{toPlainText(n.title)}</h3>
                    {n.summary && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{toPlainText(n.summary)}</p>
                    )}
                    {n.published_at && (
                      <p className="text-xs text-muted-foreground mt-3">
                        {new Date(n.published_at).toLocaleDateString()}
                      </p>
                    )}
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default MediaHub;
